(function (skyward, $)
{
    "use strict";

    skyward.modalLayer = skyward.modalLayer || {};

    /**
     * @typedef {Object} ModalLayer - An object that defines the functionality of modal layers such as modal dialogs and menus
     * @property {Array.<JQuery>} _bindingPoints - (internal) An array of elements that have events bound to them
     * @property {Array.<object>} _exposedByUnderlyingLayer - (internal) An array of data to track elements exposed by other modal layers
     * @property {Array.<object>} _exposedElements - (internal) An array of data to track elements exposed by the current modal layer
     * @property {function(JQuery):undefined} addContent - Add the content element to the modal layer
     * @property {function} beforeClose - A function that will be called before the dialog closes.  Return false to prevent the dialog from closing.
     * @property {function(string):boolean} close - Close the modal layer.  Returns if the layer was closed or not.
     * @property {boolean} closing - Is the modal layer currently closing?
     * @property {JQuery} container - The outermost element of the modal layer
     * @property {JQuery} contents - The visible dialog element within the modal layer container
     * @property {JQuery} glass - The element that covers the page when the modal layer is visible
     * @property {boolean} onscreen - Is the modal layer visible on the screen?
     * @property {object} options - Options for the modal layer.  
     *                              TODO: PR 4158200 - Document the options in a typedef
     * @property {JQuery} originalActiveElement The element that was active when the modal layer was created
     * @property {function():undefined} show - Make the modal layer visible
     * @property {Number} uniqueId - A numerical identifier for the modal layer
    */

    /**
     * @typedef {Object} ModalDialog - An object that defines a modal dialog
     * @property {function():Promise} animateModalContents - Sets the size of the dialog contents
     * @property {Promise} animationPromise - A promise that will be resolved when the dialog finishes animating its size and position
     * @property {Promise} bodyPromise - A promise that will be resolved when the dialog finishes populating its contents
     * @property {function(string):boolean} close - Close the modal layer.  Returns if the layer was closed or not.
     * @property {boolean} closed - Has the dialog been closed?
     * @property {JQuery} container - The outermost element of the modal layer
     * @property {JQuery} content - The visible dialog element within the modal layer container
     * @property {Promise} dynamicContentLoadedPromise - a promise to use in conjunction with the dynamicDialogContentSelector option
     * @property {JQuery} footer - The element that holds the buttons at the bottom of the dialog
     * @property {ModalLayer} modalLayer - The ModalLayer object for the dialog
     * @property {string} title - The text that will display at the top of the dialog's title bar
    */

    /**
     * @typedef {Object} ModalMenu - An object that defines a modal menu
     * @property {function(string):boolean} close - Close the modal layer.  Returns if the layer was closed or not.
     * @property {boolean} closed - Has the menu been closed?
     * @property {JQuery} container - The outermost element of the modal layer
     * @property {JQuery} content - The visible dialog element within the modal layer container
     * @property {ModalLayer} modalLayer - The ModalLayer object for the menu
    */

    /**
     * @typedef {Object} PositionManager - An object that manages the position of anchored modal layers
     * TODO: Document the PositionManager
    */

    var getDefaultModalLayerOptions = function ()
    {
        return {
            bindings: [],
            closeOnClick: true,
            // closeOnRecordSelected, when set to false, will prevent the layer from being closed by skyward.window.closePopup() unless the layer is specifically passed in
            closeOnRecordSelected: true,
            closeOnEscape: true,
            glass: "<div class='glass' >",
            initialFocusElement: undefined,
            preserveFocus: false,
            transparent: false,
            anchored: false,
            tailessAnchored: false,
            resizeCallback: null,
            // debugName will normally match the dialog's title, but is kept separate because the title could contain private info
            debugName: null,
            role: "dialog"
        };
    };

    var getDefaultDialogSettings = function ()
    {
        var defaultModalLayerOptions = getDefaultModalLayerOptions();
        return {
            allowNoButtons: false,
            useMobileAccessStyles: false,
            anchorTo: null,
            afterClose: null,
            beforeClose: null,
            closeOnClick: true,
            closeOnEscape: true,
            draggable: false,
            id: null,
            transparentGlass: false,
            forceClose: false,
            width: null,
            animationDuration: 100,
            // If you need to make adjustments to the content after it is loaded into the modal and not yet visible to the user. 
            // If used you must resolve this yourself or content won't show.
            beforeShowContentPromise: null,
            // If you have a selector for modal content that triggered a skyward.window.notifyDOMLoad, set it here
            // so that the content will dynamically resize as a result of the notifyDOMLoad. An example of this would be
            // themeselector.js, dynamicDialogContentSelector is set to the HTML Id of the theme browse. When the theme browse
            // is fully loaded as a result of a getBrowseRequest, browse.js triggers a notifyDOMLoad with the id of the browse.
            // The modal knows the browse is fully loaded and sizes appropriately. In the absence of setting this you would need
            // to give the modal a fixed height or be fine with it being a height that may not be ideal.
            dynamicDialogContentSelector: undefined,
            additionalClasses: "",
            closeOnRecordSelected: defaultModalLayerOptions.closeOnRecordSelected,
            // automationID can be used to identify specific dialogs in automated tests
            automationID: undefined,
            role: "dialog"
        };
    };

    var getDefaultDialogButtonSettings = function ()
    {
        return {
            id: null,
            text: "Close",
            icon: "Close",
            click: "close",
            disabled: false,
            focus: true,
            additionalClasses: ""
        };
    };

    var validRoles = [
        "dialog",
        "alertdialog",
        "menu",
        "listbox"
    ];

    var validateAriaRole = function (role)
    {
        if (!validRoles.includes(role))
        {
            console.warn(`A modal popup's ARIA role must be ${validRoles.slice(0, -1).join(", ")}, or ${validRoles[validRoles.length - 1]}, not ${role}.`);
            return "dialog";
        }
        return role;
    }
    /**
     * Shows a Reload Dialog
     *
     * @param {string} header - Text to display at the top of the dialog
     * @param {(function | Promise | string | HTMLElement | JQuery)} contents - The content of the dialog
     */
    skyward.modalLayer.showReloadDialog = function (header, contents)
    {
        var reloadButton = { text: "Reload", click: function () { skyward.window.reloadScreen(); }, icon: "Redo" };
        var closeButton = { text: "Close", click: function () { dialog.close(); }, icon: "Close" };

        if (contents == null)
        {
            contents = "An error occurred. Please try reloading the screen.";
        }
        var options =
        {
            closeOnClick: false
        };
        var dialog = skyward.modalLayer.showDialog(header, contents, [reloadButton, closeButton], options);
    };

    /**
     * Opens an Error dialog
     *
     * @param {string} title - Text to display at the top of the dialog
     * @param {(function | Promise | string | HTMLElement | JQuery)} contents - The content of the dialog
     * @param {Array.<object>} buttons - An array of buttons to add to the dialog
     * @param {object} options - A configuration object for the dialog
     *                          TODO: PR 4158200 - Document the error dialog options
     * @return {ModalDialog} The ModalDialog for the dialog that was opened
     *
     * @deprecated If possible, use a standard dialog such as showWarningDialog or showErrorDialog
     */
    skyward.modalLayer.showCriticalDialog = function (title, contents, buttons, options)
    {
        options = concatAdditionalClasses(options, "critical");
        options.role = "alertdialog";

        return modalDialog.create(title, contents, buttons, options);
    };

    /**
     * Show a standard warning dialog with confirm and cancel options.  The message will be "<b>Are you sure you want to ${action}</b> ${description}",
     * and the buttons will be ${action} and "cancel".
     * @param {string} action The action that is being warned about
     * @param {string} description A detailed description
     * @param {{ width: number?, onConfirm: function():void, id:string? }} options Additional options
     * @returns {JQuery.Promise<boolean>} A promise that will be resolved to true, if the warning was accepted, or false, if it was canceled.
     */
    skyward.modalLayer.showWarningDialog = function (action, description, optionArg)
    {
        var options = $.extend({ width: 450 }, optionArg);
        var escapedDescription = skyward.extensions.encodeHtml(description).replace(/\n/g, "<br/>");
        var contents = [
            $("<p>", { addClass: "bold", text: "Are you sure you want to " + action + "?" }),
            $("<p>&nbsp;</p>"),
            $("<p>", { html: escapedDescription })
        ];

        var approvedReasonText = "approve";
        var dialogResult = $.Deferred();

        var confirmButton = { text: action.capitalize(), icon: "Check", click: function () { dialog.close(approvedReasonText); } };
        var cancelButton = { text: "Cancel", icon: "Cancel", click: function () { dialog.close("cancel"); } };

        var dialog = modalDialog.create("Warning", contents, [confirmButton, cancelButton],
            {
                id: options.id,
                width: options.width,
                additionalClasses: "warning",
                debugName: "Warning",
                role: "alertdialog",
                afterClose: function (layer)
                {
                    const isConfirmed = layer.reasonForClose === approvedReasonText;
                    if (isConfirmed)
                    {
                        if (typeof options.onConfirm === "function")
                        {
                            options.onConfirm();
                        }
                        dialogResult.resolve();
                        return;
                    }

                    dialogResult.reject(new skyward.error.CancellationError("Canceled warning dialog"));
                }
            }
        );
        return dialogResult.promise();
    };

    /**
     * Show a standard error dialog.  The message will be "<b>${message}</b> ${description}", and the only button will be a close button.
     * @param {string} message The error
     * @param {string} description A detailed description of the error
     * @param {{ width: number?, id: string? }} options Additional options
     * @returns {JQuery.Promise} A promise that will be resolved when the dialog is closed.
     */
    skyward.modalLayer.showErrorDialog = function (message, description, optionArg)
    {
        var options = $.extend({ width: 450 }, optionArg);
        var escapedDescription = skyward.extensions.encodeHtml(description).replace(/\n/g, "<br/>");
        var contents = [
            $("<p>", { addClass: "bold", text: message }),
            $("<p>&nbsp;</p>"),
            $("<p>", { html: escapedDescription })
        ];

        var dialogResult = $.Deferred();

        var cancelButton =
        {
            text: "Close",
            icon: "Close",
            click: function (evt, dialog)
            {
                dialog.close();
                dialogResult.resolve(true);
            }
        };

        modalDialog.create("Error", contents, [cancelButton],
            {
                id: options.id,
                width: options.width,
                additionalClasses: "critical",
                debugName: "Error",
                role: "alertdialog",
                beforeClose: function (_layer, reason)
                {
                    if (reason === "escape" || reason === "glassClick")
                    {
                        dialogResult.resolve(false);
                    }
                },
                afterClose: options.afterClose
            }
        );
        return dialogResult.promise();
    };

    /**
     * Shows a basic modal dialog
     *
     * @param {string} title - Text to display at the top of the dialog
     * @param {(function | Promise | string | HTMLElement | JQuery)} contents - The content of the dialog
     * @param {Array.<object>} buttons - An array of buttons to add to the dialog
     * @param {object} options - A configuration object for the dialog
     *                          TODO: PR 4158200 - Document the dialog options
     * @return {ModalDialog} The ModalDialog for the dialog that was opened
     */
    skyward.modalLayer.showDialog = function (title, contents, buttons, options)
    {
        options = concatAdditionalClasses(options, "standard");

        return modalDialog.create(title, contents, buttons, options);
    };

    /**
     * Returns a JQuery collection of modal layer containers that exist in the DOM
     *
     * @return {JQuery} A JQuery collection of open modal layers
     */
    skyward.modalLayer.getOpenModalLayers = function ()
    {
        return $('.modalLayerContainer');
    };

    /**
     * Close the ModalLayer
     *
     * @param {JQueryInput} pModalLayer - The ModalLayer to close
     * @return {boolean} Returns true if the dialog closed
     */
    skyward.modalLayer.closeModalLayer = function (pModalLayer)
    {
        pModalLayer = $(pModalLayer);
        if (!pModalLayer.length)
        {
            return false;
        }
        var closeElement = pModalLayer.find("#cancelButton,.informationDialogClose,.glass").first();
        if (closeElement.length)
        {
            closeElement.click();
            return true;
        }
        return false;
    };

    /**
     * Opens a modal dialog with Delete/Cancel buttons, then awaits the user's response.
     *
     * @param {string} nameOfItemToDelete - A string to display as the name of the thing to delete in the message that is displayed.
     * @param {string} additionalMessage - Additional text to display underneath the delete message.
     * @param {object} options - Options for the delete dialog.
     * @return {Promise} A promise object, which is resolved or rejected when the user clicks yes or no, respectively.
     *                   Note: Script execution continues immediately. Use .done(), .fail(), and .always() to respond at the proper time.
     */
    skyward.modalLayer.confirmDelete = function (nameOfItemToDelete, additionalMessage, options)
    {
        const waitForConfirmation = $.Deferred();

        var confirmButton =
        {
            text: "Yes, Delete",
            icon: "CheckmarkConfirm",
            click: waitForConfirmation.resolve,
            additionalClasses: "confirmDelete_doDelete",
            additionalSvgClasses: "confirmDelete_doDeletePart",
            additionalTextClasses: "confirmDelete_doDeletePart"
        };

        var cancelButton =
        {
            text: "No, Cancel",
            icon: "Cancel",
            click: waitForConfirmation.reject,
            additionalClasses: "confirmDelete_cancel",
            additionalSvgClasses: "confirmDelete_cancelPart",
            additionalTextClasses: "confirmDelete_cancelPart"
        };

        let message = `<span class="modalContent__deleteHeading">Are you sure you want to delete this ${nameOfItemToDelete}?</span>`;

        if (additionalMessage != null)
        {
            message += `<div class="modalContent__deleteAdditionalText">${additionalMessage}</div>`;
        }

        if (typeof options === "object" && Array.isArray(options.referenceItems) && options.referenceItems.length > 0)
        {
            const refItems = options.referenceItems.map(item =>
            {
                return `<span class="reference-slats static"><label class="slatLabel">${item.label}</label><label class="slatData">${item.data}</label></span>`;
            });
            message = `<div class="referenceBar">${refItems.join("")}</div>${message}`;
        }

        var dialog = skyward.modalLayer.showCriticalDialog("CONFIRM DELETE", message, [confirmButton, cancelButton], { closeOnClick: false, closeOnEscape: false });
        return waitForConfirmation.always(dialog.close).promise();
    };

    /**
     * Shows a Touch Friendly dialog
     *
     * @param {string} title - Text to display at the top of the dialog
     * @param {(function | Promise | string | HTMLElement | JQuery)} contents - The content of the dialog
     * @param {JQuery} leftButtons - An JQuery collection of buttons to add to the left dialog button area
     * @param {JQuery} rightButtons - An JQuery collection of buttons to add to the right dialog button area
     * @param {object} options - A configuration object for the dialog
     *                          TODO: PR 4158200 - Document the touch friendly dialog options
     * @return {ModalDialog} The ModalDialog for the dialog that was opened
     */
    skyward.modalLayer.showTouchFriendlyDialog = function (title, contents, leftButtons, rightButtons, options)
    {
        var buttons = [];
        var setFrameworkClassname = function (definitions, position)
        {
            var frameworkClass = " modalContentFooter__button--touchFriendly js--modalContentFooter__button--" + position;
            $(definitions).each(function ()
            {
                buttons.push(this);

                this.additionalClasses == undefined ?
                    this.additionalClasses = frameworkClass :
                    this.additionalClasses += frameworkClass;
            });
        };

        setFrameworkClassname(leftButtons, "left");
        setFrameworkClassname(rightButtons, "right");

        options = concatAdditionalClasses(options, "standard");

        return modalDialog.create(title, contents, buttons, options);
    };

    /**
     * Opens a CodeEEl dialog
     *
     * @param {(HTMLElement | JQuery)} popupContent - The element that will be used as the content of the dialog
     * @param {(HTMLElement | JQuery)} textboxToFocus - The code eel textbox to place focus in when the dialog opens
     * @param {JQuery} containerToExpose - The element to expose through the glass of the dialog
     * @param {function} beforeClose - A function that will be called before the dialog closes.  Return false to prevent the dialog from closing.
     * @param {object} eelData - An object that contains data relevant to the code eel
     *                           TODO: PR 4158200 - Document the eelData options
     * @param {Array.<object>} bindings - An array of bindings objects
     * @return {ModalLayer} The ModalLayer for the code eel dialog that was opened
     */
    skyward.modalLayer.showCodeEELDialog = function (popupContent, textboxToFocus, containerToExpose, beforeClose, eelData, bindings, afterClose)
    {
        if (skyward.global.mobileAccess)
        {
            return showMobileEelDialog.apply(this, arguments);
        }
        eelData.layer = skyward.modalLayer.newModalLayer(popupContent, beforeClose,
            {
                bindings: bindings,
                expose: [containerToExpose],
                tailessAnchored: true,
                role: "listbox",
                afterClose: afterClose
            });
        var positioningOptions =
        {
            minRowsToShow: 3,
            minimumBrowseWidth: 500,
            maintainRelativePosition: true
        };

        var positioningManager = skyward.modalLayer.createModalPositioningManager(eelData.layer, containerToExpose, eelData.layer.contents, positioningOptions);
        eelData.layer.options.loadDOMCallback = positioningManager.positionEELContents;
        eelData.layer.options.resizeCallback = positioningManager.positionEELContents;
        eelData.layer.show();

        if (textboxToFocus)
        {
            textboxToFocus.focus();
        }

        return eelData.layer;
    };

    function showMobileEelDialog(popupContent, _textbox, containerToExpose, beforeClose, eelData, bindings)
    {
        if (containerToExpose.is(':animated'))
        {
            return;
        }
        containerToExpose = containerToExpose;
        var mobileEelParts;
        var ANIMATION_DURATION = 300;
        var isDMSE = containerToExpose.closest(".distinctMultiSelectEEL").addBack(".distinctMultiSelectEEL").length === 1;

        var adjustHeaderMarginForDMSE = skyward.debounce(function ()
        {
            mobileEelParts.modal.element.find('.modalHeader').css('margin-bottom', 'calc(' + mobileEelParts.textbox.element.outerHeight() + 'px + 1.5rem)');
        }, 10);

        initEelLayer();

        showEelLayer();

        return eelData.layer;

        function initEelLayer()
        {
            var modalLayerOptions =
            {
                bindings: bindings,
                expose: [containerToExpose],
                tailessAnchored: true,
                afterClose: afterLayerClose,
                resizeCallback: function ()
                {
                    if (isDMSE)
                    {
                        adjustHeaderMarginForDMSE();
                    }
                }
            };

            var textboxOffsets = containerToExpose.offset();
            var textboxWidth = containerToExpose.css('width');

            mobileEelParts =
            {
                modal:
                {
                    origPosition: { opacity: 0 },
                    newPosition: { opacity: 1 },
                    skipAfterClose: true
                },
                textbox:
                {
                    element: containerToExpose,
                    origStyle: (containerToExpose.attr('style') || ""),
                    placeholder: containerToExpose.clone(),
                    afterShowAnimate: function ()
                    {
                        if (isDMSE)
                        {
                            mobileEelParts.textbox.element.css('width', 'calc(100% - 7.75rem)');
                            adjustHeaderMarginForDMSE();
                        }
                    },
                    origPosition:
                    {
                        position: 'fixed',
                        top: textboxOffsets.top,
                        left: textboxOffsets.left,
                        width: textboxWidth,
                        margin: 0
                    },
                    newPosition:
                    {
                        position: 'fixed',
                        top: '4.7rem',
                        left: '0.7rem',
                        width: textboxWidth,
                        margin: 0
                    }
                }
            };

            eelData.layer = skyward.modalLayer.newModalLayer(popupContent, function ()
            {
                if (typeof beforeClose === 'function')
                {
                    return beforeClose();
                }
            }, modalLayerOptions);
            mobileEelParts.modal.element = eelData.layer.container.addClass('modalLayer-mobile-eel');

            if (isDMSE)
            {
                eelData.layer.container.addClass('modalLayer-mobile-eel-dmse');
            }

            addModalHeading();
        }

        function showEelLayer()
        {
            doBeforeShow();

            for (var i in mobileEelParts)
            {
                if (mobileEelParts[i].placeholder)
                {
                    mobileEelParts[i].placeholder.addClass('mobile-eel-placeholder').insertAfter(mobileEelParts[i].element);
                }

                mobileEelParts[i]
                    .element.css(mobileEelParts[i].origPosition)
                    .stop()
                    .animate(mobileEelParts[i].newPosition, ANIMATION_DURATION, null, (function (animatedPart)
                    {
                        return function ()
                        {
                            if (animatedPart.afterShowAnimate)
                            {
                                animatedPart.afterShowAnimate();
                            }
                        };

                    })(mobileEelParts[i]));
            }

            if (skyward.global.isIosApp)
            {
                $(document.scrollingElement).animate({ scrollTop: 0 }, ANIMATION_DURATION);
            }

            eelData.layer.show();
        }

        function addModalHeading()
        {
            $("<div>", { 'class': 'title modalHeader' }).append($('<span>', { 'class': 'modalHeaderText', 'html': getEelLabel() })).prependTo(mobileEelParts.modal.element.find('.modalLayer'));

            if (isDMSE)
            {
                adjustHeaderMarginForDMSE();
            }
        }

        function getEelLabel()
        {
            var label = "";
            var controlLabel = containerToExpose.attr('data-validate-control-for');
            if (controlLabel)
            {
                controlLabel = containerToExpose.closest('fieldset').find('[data-validate-text-for=' + controlLabel.replace(/\./g, '\\.') + ']');
                if (controlLabel.length)
                {
                    label = controlLabel.text().replace(/\*/g, '');
                }
            }

            if (label.indexOf('Select') != 0)
            {
                label = 'Select ' + label;
            }

            return label;
        }

        function closeEelLayer(event)
        {
            event.data.layer.glass.trigger('click');
        }

        function doBeforeShow()
        {
            setSoftKeyboardEventHandler();
            resizeForSoftKeyboard();
            $(document.body).addClass('mobileEelOpen');
            skyward.mobile.scrollToTop();
        }

        function doAfterClose()
        {
            $(document.body)
                .off('softKeyboardToggle', resizeForSoftKeyboard)
                .off('click', '.distinctMultiSelectSearchButton', closeEelLayer)
                .removeClass('mobileEelOpen');
        }

        function setSoftKeyboardEventHandler()
        {
            var softKeyboardToggleEvents = $._data(document.body, "events").softKeyboardToggle || [];
            var hasEventHandler = false;

            for (var i = 0; i < softKeyboardToggleEvents.length; i++)
            {
                if (softKeyboardToggleEvents[i].handler === resizeForSoftKeyboard)
                {
                    hasEventHandler = true;
                    break;
                }
            }

            if (!hasEventHandler)
            {
                $(document.body).on('softKeyboardToggle', resizeForSoftKeyboard);
            }
        }

        function resizeForSoftKeyboard()
        {
            var keyboardHeight = 0;
            var body = $(document.body);
            if (body.hasClass("soft-keyboard-open"))
            {
                keyboardHeight = body.attr("data-soft-keyboard-height") || 0;
            }
            mobileEelParts.modal.element.css({ 'top': '0px', 'bottom': (keyboardHeight + 'px'), 'height': 'auto' });
        }

        function afterLayerClose()
        {
            var debouncedAfterClose = skyward.debounce(doAfterClose, 10);
            var elementData = {};

            for (var i in mobileEelParts)
            {
                if (mobileEelParts[i].skipAfterClose)
                {
                    continue;
                }

                elementData[mobileEelParts[i].element[0]] = mobileEelParts[i];

                mobileEelParts[i].element
                    .css(mobileEelParts[i].newPosition)
                    .stop()
                    .animate(mobileEelParts[i].origPosition, ANIMATION_DURATION, function ()
                    {
                        var eelData = elementData[this];
                        if (eelData)
                        {
                            if (eelData.placeholder)
                            {
                                eelData.placeholder.remove();
                            }
                            eelData.element.attr('style', eelData.origStyle);
                        }
                        debouncedAfterClose();
                    });
            }
        }
    }

    /**
     * Shows a Tile View Results dialog
     *
     * @param {(HTMLElement | JQuery)} popupContent - The element that will be used as the content of the dialog
     * @param {JQuery} searchBox - The element to expose through the glass of the dialog and anchor the dialog to
     * @param {function} beforeClose - A function that will be called before the dialog closes.  Return false to prevent the dialog from closing.
     * @param {Array.<object>} bindings - An array of bindings objects
     * @return {ModalLayer} The ModalLayer that was created
     */
    skyward.modalLayer.showTileViewResults = function (popupContent, searchBox, beforeClose, bindings)
    {
        var layer = skyward.modalLayer.newModalLayer(popupContent, beforeClose, { bindings: bindings, expose: [searchBox], preserveFocus: true, role: "listbox" });

        var positioningOptions =
        {
            getRows: function () { return layer.contents.find('.templatedResult, .toolbar--browseControls.tileviewEEL'); },
            getHeaderRow: function () { return layer.contents.find('.templatedResult').first(); }
        };

        var positioningManager = skyward.modalLayer.createModalPositioningManager(layer, searchBox, layer.contents, positioningOptions);

        layer.options.resizeCallback = positioningManager.positionEELContents;

        skyward.window.registerLayoutResizeCallback(function ()
        {
            layer.options.resizeCallback();
        });
        return layer;
    };

    /**
     * Shows an Information dialog
     *
     * @param {string} title - Text to display at the top of the dialog
     * @param {(function | Promise | string | HTMLElement | JQuery)} contents - The content of the dialog
     * @param {Array.<object>} buttons - An array of buttons to add to the dialog
     * @param {object} options - A configuration object for the dialog
     *                           TODO: PR 4158200 - Document the dialog options
     * @return {ModalDialog} The ModalDialog for the dialog that was opened
     */
    skyward.modalLayer.showInformationDialog = function (title, contents, buttons, options)
    {
        options = concatAdditionalClasses(options, "standard");

        var dialog = modalDialog.create(title, contents, buttons, options);
        if (!dialog.title.find('.informationDialogClose').length)
        {
            var informationDialogClose = $("<div>", { 'class': 'informationDialogClose' });
            informationDialogClose.attr("tabindex", 0);

            var closeSvg = getSVG("Close", "MessageBox").addClass("svgNoHover");
            informationDialogClose.append(closeSvg);

            dialog.title.addClass("hasClose");
            dialog.title.append(informationDialogClose);

            informationDialogClose.click(function ()
            {
                dialog.close();
            });
        }

        dialog.content.addClass('noFooter informationDialogContent');
        dialog.footer.remove();

        return dialog;
    };

    /**
     * Shows an Information Bubble dialog
     *
     * @param {string} title - Text to display at the top of the dialog
     * @param {(function | Promise | string | HTMLElement | JQuery)} contents - The content of the dialog
     * @param {Array.<object>} buttons - An array of buttons to add to the dialog
     * @param {object} options - A configuration object for the dialog
     *                           TODO: PR 4158200 - Document the information bubble options
     * @return {ModalDialog} The ModalDialog for the dialog that was opened
     */
    skyward.modalLayer.showInformationBubble = function (title, contents, buttons, options)
    {
        options = concatAdditionalClasses(options, "standard");

        var dialog = modalDialog.create(title, contents, buttons, options);
        dialog.content.addClass('noFooter');
        dialog.footer.remove();
        dialog.container.addClass('noFooter');
        return dialog;
    };

    /**
     * Shows a Processing dialog
     *
     * @param {string} title - Text to display at the top of the dialog
     * @param {(function | Promise | string | HTMLElement | JQuery)} contents - The content of the dialog
     * @param {Array.<object>} buttons - An array of buttons to add to the dialog
     * @param {object} options - A configuration object for the dialog
     *                           TODO: PR 4158200 - Document the processing dialog options
     * @return {ModalDialog} The ModalDialog for the dialog that was opened
     */
    skyward.modalLayer.showProcessingDialog = function (title, contents, buttons, options)
    {
        options = concatAdditionalClasses(options, "standard");

        options.automationID = 'processingDialog';
        var dialog = modalDialog.create(title, contents, buttons, options);

        if (!skyward.global.mobileAccess)
        {
            const helpCenterWorkflowButton = $('.js-workflowHelpCenterButton');

            if (helpCenterWorkflowButton.length > 0)
            {
                const helpCenterButton = $('<a>', { 'class': 'messageBoxTitleButton', 'href': helpCenterWorkflowButton.prop('href') ?? 'https://hub.skyward.com/sso/helpcenter/qproxy', 'target': '_blank' });
                const helpCenterText = $('<div class="messageBoxTitleButton__text">Help<br/>Center</div>');
                helpCenterButton.append(helpCenterText);

                const helpCenterSvg = getSVG("Help", "MessageBox").addClass("messageBoxTitleButton__svg svgNoHover");
                helpCenterButton.prepend(helpCenterSvg);
                dialog.title.append(helpCenterButton);
            }

            const newWindowButton = $('<a>', { 'class': 'messageBoxTitleButton', 'href': 'javascript:void(0)' });
            const newWindowText = $('<div class="messageBoxTitleButton__text">New<br/>Window</div>');
            newWindowButton.append(newWindowText);

            const newWindowSvg = getSVG("NewWindow", "MessageBox").addClass("messageBoxTitleButton__svg svgNoHover");
            newWindowButton.prepend(newWindowSvg);
            dialog.title.append(newWindowButton);

            newWindowButton.on('click', function ()
            {
                const url = window.location.pathname;
                let urlArr;

                if (url != null)
                {
                    urlArr = url.split('/');
                    if (urlArr.length <= 5)
                    {
                        urlArr[5] = null;
                    }
                }

                skyward.window.loadContent('popout', urlArr[2], urlArr[3], urlArr[4] || "index", urlArr[5]);
            });
        }

        return dialog;
    };

    var defaultMenuSettings =
    {
        beforeClose: null,
        afterClose: null,
        debugName: null
    };

    var defaultMenuItemSettings =
    {
        type: "item", // Can also be set to title
        id: null,
        icon: null,
        click: null,
        data: null,
        attributes: null,
        disabled: false,
        selected: false
    };

    /**
     * Shows a menu dialog
     * 
     * @param {Array.<object>} menuItems - An array of menu item objects that define the items in the menu
     * @param {(HTMLElement | JQuery | string)} anchorTo - The element that the menu will be anchored to
     * @param {object} options - A configuration object for the menu
     *                           TODO: PR 4158200 - Document the menu options
     * @return {ModalMenu} The ModalMenu that was created
     */
    skyward.modalLayer.showMenu = function (menuItems, anchorTo, options)
    {
        return modalMenu.create(menuItems, anchorTo, options);
    };

    var modalDialog = (function ()
    {
        var modalDialog = {};

        modalDialog.create = function (title, contents, buttonDefinitions, options)
        {
            var defaultDialogSettings = getDefaultDialogSettings();
            var settings = $.extend({}, defaultDialogSettings, options);

            if (buttonDefinitions)
            {
                $.each(buttonDefinitions, function (index, button)
                {
                    if (button?.text === "Save")
                    {
                        settings.closeOnClick = false;
                        return false;
                    }
                });
            }

            if (!(settings.anchorTo instanceof $))
            {
                settings.anchorTo = $(settings.anchorTo);
            }

            var dialog = {};

            dialog.title = $("<div>", { class: ("messageBox title modalHeader " + settings.additionalClasses).trim() }).append($("<span>", { class: "modalHeaderText", "html": title })).addIds();
            dialog.content = $("<div>", { class: "messageBox modal content" }).addIds();
            dialog.footer = $("<div>", { class: ("modalContentFooter " + settings.additionalClasses).trim() }).addIds();
            dialog.container = $("<div>",
                {
                    id: settings.id,
                    "class": "messageBox skywardContentPanel",
                    "data-automation-id": settings.automationID,
                    "aria-labelledby": dialog.title.attr("id"),
                    "aria-modal": true,
                    append: [dialog.title, dialog.content, dialog.footer]
                });
            if (settings.role == "alertdialog")
            {
                dialog.container.attr("aria-describedby", dialog.content.attr("id"));
            }

            if (settings.hideOverflow)
            {
                dialog.content.css('overflow', 'hidden');
            }

            if (settings.useSmallWidth)
            {
                dialog.container.addClass("smallWidth");
                dialog.content.addClass("contentSmallWidth");
            }

            dialog.closed = false;

            var deferredAnimation = $.Deferred();
            dialog.animationPromise = deferredAnimation.promise();

            var positioningManager = skyward.modalLayer.createModalPositioningManager(dialog, settings.anchorTo, dialog.content);

            dialog.animateModalContents = function ()
            {
                positioningManager.positionModalContents();
                if (positioningManager.isAnchored())
                {
                    dialog.content.outerHeight(positioningManager.measurements.allowedContentHeight || 'auto');
                }
                deferredAnimation.resolve();
                return deferredAnimation.promise();
            };

            dialog.close = function (reason)
            {
                dialog.modalLayer.close(reason);
                dialog.closed = true;
            };

            const populateButtonsPromise = populateButtons(buttonDefinitions, dialog.close, settings, dialog);
            $.when(populateButtonsPromise).then(function ()
            {
                if ((skyward.global.mobileAccess || settings.useMobileAccessStyles) && !dialog.footer.children('button').length)
                {
                    dialog.footer.remove();
                }
            });

            const deferredDynamicContentLoaded = $.Deferred();
            dialog.dynamicContentLoadedPromise = deferredDynamicContentLoaded.promise();
            const animateAfterLoadDOMCallback = function (selector)
            {
                if (selector === settings.dynamicDialogContentSelector)
                {
                    dialog.animateModalContents().then(function ()
                    {
                        deferredDynamicContentLoaded.resolve();
                        dialog.content.show();
                        skyward.window.notifyResize();
                    });
                }
            };

            dialog.modalLayer = skyward.modalLayer.newModalLayer(dialog.container, settings.beforeClose,
                {
                    afterClose: settings.afterClose,
                    closeOnClick: settings.closeOnClick,
                    closeOnEscape: settings.closeOnEscape,
                    forceClose: settings.forceClose,
                    transparent: settings.transparentGlass,
                    resizeCallback: positioningManager.positionModalContents,
                    loadDOMCallback: settings.dynamicDialogContentSelector ? animateAfterLoadDOMCallback : false,
                    closeOnRecordSelected: settings.closeOnRecordSelected,
                    expose: settings.expose,
                    role: settings.role,
                    initialFocusElement: settings.initialFocusElement,
                    preserveFocus: settings.preserveFocus,
                    additionalContainerClasses: settings.additionalContainerClasses
                });

            const publicBodyDeferred = $.Deferred();
            dialog.bodyPromise = publicBodyDeferred.promise();

            dialog.container.css('display', 'none');

            const bodyPromise = populateBody(dialog.content, contents);

            function contentLoadedSuccess()
            {
                dialog.modalLayer.show();
                dialog.container.css('display', '');

                dialog.content.trigger("contentLoaded");

                if (!dialog.container.attr('id'))
                {
                    dialog.container.attr('id', 'modalLayerDialog' + dialog.modalLayer.uniqueId);
                }

                const containerId = '#' + dialog.container.attr('id');
                skyward.window.notifyDOMLoad(containerId + ' > .content');
                if (!skyward.global.mobileAccess)
                {
                    focusDialogAndSetDescription(dialog);
                }
            }

            $.when(bodyPromise).then(function ()
            {
                // in case of slow network, the user can click the glass before the modal appears, closing it before it appears. 
                // no need to do any showing in this case
                if (!dialog.modalLayer.closing)
                {
                    if (settings.beforeShowContentPromise)
                    {
                        $.when(settings.beforeShowContentPromise).then(function ()
                        {
                            contentLoadedSuccess();
                        });
                    }
                    else
                    {
                        contentLoadedSuccess();
                    }

                    if (!settings.dynamicDialogContentSelector)
                    {
                        dialog.content.css('visibility', 'visible'); // we need to have content take up space so fieldset.js can lineup fieldsets
                        dialog.animateModalContents();
                    }

                    dialog.content.on('treeBrowseContentLoaded', function ()
                    {
                        dialog.animateModalContents();
                    });

                    // Don't set initial focus in mobile mode, it will display the onscreen keyboard
                    if (!skyward.global.mobileAccess)
                    {
                        publicBodyDeferred.then(function ()
                        {
                            dialog.container.find("[autofocus]").first().focus();
                        });
                    }
                }

                publicBodyDeferred.resolve();
                
            }, function ()
            {
                dialog.close();
            });

            if (settings.draggable && !skyward.global.mobileAccess)
            {
                makeDraggable(dialog.container, { handle: ".modalHeader.title" });
            }

            $.when(populateButtonsPromise, bodyPromise).done(initLayerPseudoForm);

            // a registerLoadDOMCallback is necessary to account for content added after the dialog loads, such as browses.
            skyward.window.registerLoadDOMCallback(checkInitLayerPseudoForm);

            if (settings.width)
            {
                dialog.container.css('width', settings.width);
            }

            if (settings.height)
            {
                dialog.container.css('height', settings.height);
            }

            return dialog;

            function checkInitLayerPseudoForm()
            {
                if (!dialog || !dialog.modalLayer.onscreen)
                {
                    skyward.window.removeLoadDOMCallback(checkInitLayerPseudoForm);
                    return;
                }
                const loadedElement = $(this).get(0);
                if (loadedElement && dialog.container.length && dialog.container[0].contains(loadedElement))
                {
                    if (initLayerPseudoForm() !== false)
                    {
                        skyward.window.removeLoadDOMCallback(checkInitLayerPseudoForm);
                    }
                }
            }

            function initLayerPseudoForm()
            {
                if (!dialog || !dialog.modalLayer.onscreen)
                {
                    return;
                }

                let hasSaveButton;
                if (dialog.content.hasClass("noFooter"))
                {
                    hasSaveButton = dialog.content.find(".save").length > 0;
                }
                else
                {
                    hasSaveButton = dialog.footer.find(".save").length > 0;
                }

                if (hasSaveButton && dialog.content.find(":input").not("[type=hidden]").length)
                {
                    const layerPseudoForm = dialog.content.closest(".layerPseudoForm");
                    const titleId = dialog.title.find(".modalHeaderText").addIds().attr("id");
                    layerPseudoForm.attr("role", "form");
                    layerPseudoForm.attr("aria-labelledby", titleId);
                    return true;
                }
                return false;
            }
        };

        var populateBody = function (content, contents)
        {
            if (typeof contents === "function")
            {
                return appendDeferred(contents());
            }
            else if (contents && typeof contents.then === "function")
            {
                return appendDeferred(contents);
            }
            else
            {
                if (typeof contents === "string")
                {
                    var contentsParts = contents.split("<textarea");
                    var newString = "";
                    newString = contentsParts[0].replace(/\n/gi, "<br />");
                    for (var i = 1; i < contentsParts.length; i++)
                    {
                        var subContentsParts = contentsParts[i].split("</textarea>");
                        newString += "<textarea" + subContentsParts[0];
                        if (subContentsParts.length > 1)
                        {
                            newString += "</textarea>" + subContentsParts[1].replace(/\n/gi, "<br />");
                        }
                    }
                    contents = newString;
                }
                // Some callers assume strings or jquery elements that are passed in will be inserted into the dom immediately,
                // before newModalLayer() returns.  This is the best we can do without loading scripts synchronously.
                var contentParts = skyward.global.processScriptsAndHtmlSeparately(contents);
                content.append(contentParts.html);
                return contentParts.scriptsLoaded;
            }

            function appendDeferred(unwrappedContents)
            {
                return $.when(unwrappedContents)
                    .then(skyward.global.processScriptHtml)
                    .then(function (functionResult)
                    {
                        content.append(functionResult);
                    });
            }
        };

        var populateButtons = function (buttonDefinitions, closeFunction, settings, dialog)
        {
            var footer = dialog.footer;
            var header = dialog.title;

            return $.when(getButtonsFromDefinitions(buttonDefinitions, closeFunction, settings, dialog)).done(function (buttons)
            {
                if (!buttons)
                {
                    return false;
                }

                if (buttons && buttons.mobileClose)
                {
                    header.addClass("hasClose");
                    header.append(buttons.mobileClose);
                }

                $(buttons.left).each(function ()
                {
                    footer.append($(this));
                });

                footer.append($("<div>", { 'class': "modalContentFooter__leftRightButtonGroupSeparator" }));

                $(buttons.right).each(function ()
                {
                    footer.append($(this));
                });
            });
        };

        var getButtonsFromDefinitions = function (definitions, closeFunction, settings, dialog)
        {
            var deferred = $.Deferred();

            if ((skyward.global.mobileAccess || settings.useMobileAccessStyles) && !definitions)
            {
                definitions = [{ text: 'Close' }];
            }

            if (typeof definitions === "function")
            {
                $.when(definitions()).done(function (functionResult)
                {
                    deferred.resolve(createButtons(functionResult, closeFunction, settings, dialog));
                });
            }
            else
            {
                if (!$.isArray(definitions))
                {
                    definitions = [definitions];
                }

                deferred.resolve(createButtons(definitions, closeFunction, settings, dialog));
            }

            return deferred.promise();
        };

        var createButtons = function (buttonDefinitions, closeFunction, settings, dialog)
        {
            var buttons = {};
            buttons.right = [];
            buttons.left = [];
            var noVisibleButtons = true;

            if (skyward.global.mobileAccess || settings.useMobileAccessStyles)
            {
                const closeButtonLabels = ["close", "cancel", "done", "ok"];

                let foundCloseButton = undefined;

                if (buttonDefinitions.length > 0)
                {
                    foundCloseButton = buttonDefinitions.reduce((a, e) =>
                    {
                        var ia = closeButtonLabels.indexOf(a?.text.toLowerCase()) ?? -1;
                        var ie = closeButtonLabels.indexOf(e.text.toLowerCase());
                        if (ie < ia && ie >= 0)
                        {
                            return e;
                        }
                        if (ia > -1)
                        {
                            return a;
                        }
                    });
                }

                if (foundCloseButton !== undefined)
                {
                    buttonDefinitions = buttonDefinitions.filter((e) =>
                    {
                        if (e !== foundCloseButton)
                        {
                            return e;
                        }
                        createCloseButton(e);
                    });
                }
            }

            for (var i = 0; i < buttonDefinitions.length; i++)
            {
                var definition = buttonDefinitions[i];
                if (definition)
                {
                    var button = createButton(definition, closeFunction, settings, dialog, false);

                    if (button != null)
                    {
                        noVisibleButtons = false;
                        if (button.hasClass('js--modalContentFooter__button--left'))
                        {
                            buttons.left.push(button);
                        }
                        else
                        {
                            buttons.right.push(button);
                        }
                    }
                }
            }

            // Push on a default ok button, if none were passed in
            if (noVisibleButtons && !settings.allowNoButtons)
            {
                if (skyward.global.mobileAccess || settings.useMobileAccessStyles)
                {
                    createCloseButton();
                }
                else
                {
                    buttons.right.push(createButton({}, closeFunction, undefined, undefined, false));
                }
            }

            function createCloseButton(buttonDefinition)
            {
                var button = createButton($.extend(buttonDefinition, { text: "Close", icon: "Close" }), closeFunction, settings, dialog, true);

                if (button)
                {
                    noVisibleButtons = false;
                    buttons.mobileClose = button;
                }
            }

            return buttons;
        };

        var createButton = function (buttonDefinition, closeFunction, settings, dialog, isCloseButton)
        {
            if (buttonDefinition == null)
            {
                return null;
            }

            var defaultDialogButtonSettings = getDefaultDialogButtonSettings();

            buttonDefinition = $.extend({}, defaultDialogButtonSettings, buttonDefinition);

            var buttonAttributes =
            {
                id: buttonDefinition.id,
                class: (isCloseButton ? "informationDialogClose " : "modalContentFooter__button ") + buttonDefinition.additionalClasses
            };

            var button = $("<button>", buttonAttributes);

            if (buttonDefinition.data)
            {
                button.data(buttonDefinition.data);
            }

            if (buttonDefinition.automationID)
            {
                button.attr("data-automation-id", buttonDefinition.automationID);
            }
            else if (settings && settings.automationID && buttonDefinition.text)
            {
                button.attr("data-automation-id", `${settings.automationID}-${buttonDefinition.text.toLowerCase().replace(/ /g, "_")}-button`);
            }

            if (isCloseButton)
            {
                var buttonSvg = getSVG(buttonDefinition.icon, "MessageBox").addClass("buttonSvg");
                button.append(buttonSvg);
            }
            else
            {
                var buttonContent = $("<div>", { 'class': "button__fireFoxFlexContentFixForButton" });
                button.append(buttonContent);
                var buttonSvg = getSVG(buttonDefinition.icon, "MessageBox").addClass("buttonSvg");
                buttonContent.append(buttonSvg);

                var text = $("<span>", { 'class': buttonDefinition.icon != null ? "modalContentFooter__buttonText" : "modalContentFooter__buttonText--noIcon" }).append(buttonDefinition.text);
                if (buttonDefinition.additionalTextClasses)
                {
                    text.addClass(buttonDefinition.additionalTextClasses);
                }
                buttonContent.append(text);
            }
            setClickEvent(button, buttonDefinition.click, closeFunction, dialog);

            if (buttonDefinition.focus)
            {
                button.attr("autofocus", "true");
            }

            return button;
        };

        var defaultDraggableOptions =
        {
            scroll: false,
            handle: ".title"
        };

        var makeDraggable = function (container, options)
        {
            var draggableSettings = $.extend({}, defaultDraggableOptions, options);

            container.draggable(
                {
                    scroll: draggableSettings.scroll,
                    handle: draggableSettings.handle,
                    cancel: draggableSettings.cancel
                });

            skyward.modalLayer.setupDraggableLimits(container);

            container.find(draggableSettings.handle).css("cursor", "move");
        };
        return modalDialog;
    })();

    /**
     * Adds events to the dialog container to prevent it from being moved beyond the edge of the screen.
     *
     * @param {JQuery} container - A string set draggable limits on
     */
    skyward.modalLayer.setupDraggableLimits = function (container)
    {
        // Set draggable containment. Stop dragging when mouse hits edge of window or when draggable
        // will be less than the specified minimum pixels on screen.
        container.on('mousedown', function (event)
        {
            var master = $('#master');
            var minimumOnScreen = container.find('.modalHeader').outerHeight();

            var popupOffset = container.offset();
            var mouseOffset =
            {
                top: event.pageY - popupOffset.top,
                left: event.pageX - popupOffset.left
            };

            var masterWidth = master.width();
            var masterHeight = master.height();

            var minimumX1 = (container.width() - minimumOnScreen) * -1;
            var maximumX2 = masterWidth - minimumOnScreen;
            var maximumY2 = masterHeight - minimumOnScreen;

            var x1 = mouseOffset.left * -1;
            var y1 = 0;
            var x2 = masterWidth - mouseOffset.left;
            var y2 = masterHeight - mouseOffset.top;

            if (x1 < minimumX1)
            {
                x1 = minimumX1;
            }

            if (x2 > maximumX2)
            {
                x2 = maximumX2;
            }

            if (y2 > maximumY2)
            {
                y2 = maximumY2;
            }

            container.draggable('option', 'containment', [x1, y1, x2, y2]);
        });
    };

    var modalMenu = (function ()
    {
        var modalMenu = {};

        modalMenu.create = function (menuItems, anchorTo, options)
        {
            var settings = $.extend({}, defaultMenuSettings, options);
            var menu = {};
            anchorTo = $(anchorTo);

            menu.container = $("<div>", { 'class': 'menuPopup' });
            menu.content = $("<div>", { 'class': 'menuPopup modal content' });
            menu.container.append(menu.content);


            menu.close = function ()
            {
                menu.modalLayer.options.forceClose = true;
                menu.modalLayer.close();
                menu.closed = true;
            };

            menu.closed = false;

            var menuElements = menuItems.map(menuItem => createMenuItem(menuItem, menu.close, menu));
            menu.content.append(menuElements);

            if (menuItems.length > 0 && menuItems[0].type === "title")
            {
                menu.container.attr("aria-labelledby", menuElements[0].addIds().attr("id"));
            }

            var positioningManager = skyward.modalLayer.createModalPositioningManager(menu, anchorTo, menu.content);

            menu.modalLayer = skyward.modalLayer.newModalLayer(menu.container, settings.beforeClose,
                {
                    afterClose: settings.afterClose,
                    resizeCallback: function ()
                    {
                        positioningManager.positionModalContents();
                        setMenuWidth(menu.container);
                    },
                    debugName: settings.debugName,
                    role: "menu"
                });

            positioningManager.positionModalContents();
            setMenuWidth(menu.container);
            menu.modalLayer.show();
            return menu;
        };

        var createMenuItem = function (menuItemDefinition, closeFunction, menu)
        {
            if (menuItemDefinition == null)
            {
                return null;
            }

            menuItemDefinition = $.extend({}, defaultMenuItemSettings, menuItemDefinition);

            if (menuItemDefinition.type.toLowerCase() === "custom")
            {
                if (menuItemDefinition.content == null)
                {
                    return null;
                }
                if (typeof menuItemDefinition.content === "function")
                {
                    return menuItemDefinition.content();
                }
                return menuItemDefinition.content;
            }

            var isTitle = menuItemDefinition.type.toLowerCase() == "title";
            var iconSpan = $("<span>", { 'class': "menuPopupItemIcon", "aria-hidden": true });
            var textSpan = $("<span>", { 'class': "anchorText textAfter svgGroup" }).text(menuItemDefinition.text);

            iconSpan.append(getSVG(menuItemDefinition.icon, "MessageBox"));

            let defaultRole;
            let defaultHasPopup;
            if (menuItemDefinition.target === "popup")
            {
                defaultRole = "button";
                defaultHasPopup = "dialog";
            }
            else
            {
                defaultRole = "link";
                defaultHasPopup = "false";
            }
            var anchorAttributes =
            {
                id: menuItemDefinition.id,
                href: "javascript:void(0);",
                'class': isTitle ? "menuPopupTitle" : "menuPopupItem",
                role: isTitle ? undefined : "menuitem",
                "aria-haspopup": menuItemDefinition.hasPopup || defaultHasPopup,
            };

            if (menuItemDefinition.selected)
            {
                anchorAttributes['class'] += " selected";
            }

            if (menuItemDefinition.attributes)
            {
                for (var attributeName in menuItemDefinition.attributes)
                {
                    anchorAttributes[attributeName] = menuItemDefinition.attributes[attributeName];
                }
            }

            var menuItem = isTitle ? $("<div>", anchorAttributes) : $("<a>", anchorAttributes);
            menuItem.append(iconSpan, textSpan);

            if (menuItemDefinition.disabled)
            {
                menuItem.addClass("ui-disabled");
            }

            if (menuItemDefinition.data != null)
            {
                menuItem.data(menuItemDefinition.data);
            }

            setClickEvent(menuItem, menuItemDefinition.click, closeFunction, menu);

            menuItem.on("click", function ()
            {
                if (!menuItem.prop("disabled") && !menuItem.hasClass("ui-disabled"))
                {
                    closeFunction();
                }
            });

            return menuItem;
        };

        var setMenuWidth = function (menu)
        {
            var content = menu.find('.content');
            var greatestChildWidth = 0;
            menu.children().each(function ()
            {
                var menuContent = $(this);
                menuContent.width(''); // reset content width so we don't grow for the scrollbar each time
                var childWidth = menuContent.outerWidth(true) + 1;  // Add one to compensate for IE rounding error on outerWidth
                if (childWidth > greatestChildWidth)
                {
                    greatestChildWidth = childWidth;
                }
            });

            if (Math.ceil(content.height()) != content[0].scrollHeight)
            {
                greatestChildWidth += 20; // Accommodate for scroll bar
            }
            content.width(greatestChildWidth);
        };

        return modalMenu;
    })();

    var getSVG = function (icon, type)
    {
        if (typeof icon === 'string')
        {
            return skyward.svg.getSvg(icon, type);
        }
        else if (typeof icon === 'object')
        {
            return $(icon).clone();
        }
        else
        {
            return null;
        }
    };

    const getPrecedingElements = function (startingElement, root)
    {
        if (!root || !startingElement || startingElement.compareDocumentPosition(root) & Node.DOCUMENT_POSITION_PRECEDING != Node.DOCUMENT_POSITION_PRECEDING)
        {
            return [];
        }
        const result = [];
        for (let currentElement = startingElement; currentElement != root; currentElement = currentElement.parentElement)
        {
            for (; currentElement.previousElementSibling != null; currentElement = currentElement.previousElementSibling)
            {
                result.push(currentElement.previousElementSibling);
            }
        }
        result.reverse();
        return result;
    };

    const focusDialogAndSetDescription = function (dialog)
    {
        // In case focus starts on a button or an element within the dialog, make sure the contents of the dialog
        // before the focused element get read out, by adding them to the dialog's aria-description.
        const focusedElement = skyward.dialogFocusManager.setFocus(dialog.modalLayer);
        if (dialog.content.has(focusedElement).length == 0)
        {
            dialog.container.attr("aria-describedby", dialog.content.addIds().attr("id"));
        }
        else
        {
            const preamble = $(getPrecedingElements(dialog.content.find(":focusable").get(0), dialog.content.get(0)))
                .filter(":visible:not(fieldset *, .contentMessageArea)")
                .toArray();
            dialog.container.attr("aria-describedby", preamble.map(el => $(el).addIds().attr("id")).join(" "));
        }
    };

    var setClickEvent = function (clickable, event, closeFunction, modal)
    {
        if (typeof event === 'function')
        {
            clickable.on("click", function ()
            {
                if (!clickable.prop("disabled") && !clickable.hasClass("ui-disabled"))
                {
                    var i = arguments.length, args = new Array(i); while (i--) args[i] = arguments[i];
                    args.push(modal);
                    event.apply(clickable, args);
                }
            });
        }
        else if (typeof event === 'string' && event.toLowerCase() !== "close")
        {
            clickable.attr("onclick", event);
        }
        else
        {
            clickable.on("click", closeFunction);
        }
    };

    /**
     * Backbone API to define, create, bind, handle and destroy any modal interface components
     *
     * @param {HTMLElement | JQuery} contents - Element to be inserted into the new modal layer
     * @param {function} beforeClose - Function to be called before layer closes. Return (false) to keep the layer
     * @param {object} options - Advanced options - See documentation at head of function
     *                           TODO: PR 4158200 - Document the modal layer options
     * @return {ModalLayer} The completed 'layer object', which contains references to all elements, functions, and objects needed during the lifespan of the layer
     */
    skyward.modalLayer.newModalLayer = function (contents, beforeClose, options)
    {
        // Establish layer data
        const layerId = $(document.body).children(".modalLayerContainer").length;
        const defaultModalLayerOptions = getDefaultModalLayerOptions();
        let layerPseudoForm = undefined;
        let layer =
        {
            _bindingPoints: [],
            _exposedByUnderlyingLayer: [],
            _exposedElements: [],
            addContent: function (content)
            {
                if (!content.hasClass("glass"))
                {
                    content.addClass("modalLayer");
                    if (options?.additionalContainerClasses)
                    {
                        content.addClass(options.additionalContainerClasses);
                    }
                    if (!content.attr("role"))
                    {
                        content.attr("role", validateAriaRole(layer.options.role));
                    }
                    if (!content.attr("aria-modal"))
                    {
                        content.attr("aria-modal", true);
                    }
                    // The layerPseudoForm is used for adding role=form to dialogs when appropriate for accessibility
                    if (layerPseudoForm === undefined)
                    {
                        layerPseudoForm = $("<div>", { class: "layerPseudoForm", append: content });
                        this.container.append(layerPseudoForm);
                        return;
                    }
                }
                this.container.append(content);

                $.extend(layer.contents, content);
            },
            beforeClose: beforeClose,
            container: $("<div>", { class: "modalLayerContainer", "data-layerid": layerId }),
            contents: null,
            glass: null,
            onscreen: false,
            closing: false,
            options: $.extend({}, defaultModalLayerOptions, options),
            originalActiveElement: document.activeElement,
            show: function ()
            {
                if (!layer)
                {
                    skyward.error.reportException(new window.SkywardException("layer is undefined within show function.", `layerId: ${layerId}`));
                    return;
                }
                if (layer.options.anchored)
                {
                    layer.contents.addClass('anchored');
                    if (layer.options.tailessAnchored)
                    {
                        layer.contents.addClass('noAnchoredTail');
                    }
                }
                layer.contents.addClass('columnLayoutWrapper--modalLayer');
                layer.container.show();
                if (layer.options.loadDOMCallback && typeof layer.options.loadDOMCallback === 'function')
                {
                    skyward.window.registerLoadDOMCallback(layer.options.loadDOMCallback);
                }
                if (layer.options.resizeCallback && typeof layer.options.resizeCallback === 'function')
                {
                    skyward.window.registerLayoutResizeCallback(layer.options.resizeCallback);
                }
                if (!skyward.global.mobileAccess)
                {
                    skyward.dialogFocusManager.setFocus(layer);
                }
            },
            uniqueId: layerId
        };

        if (layer.options.tailessAnchored)
        {
            layer.options.anchored = true;
        }

        if (layer.options.loadDOMCallback)
        {
            skyward.window.registerLoadDOMCallback(layer.options.loadDOMCallback);
        }

        layer.glass = $(layer.options.glass).attr("id", "modalLayerGlass" + layer.uniqueId);

        if (options != null && options.transparent == true)
        {
            $(layer.glass).addClass("transparent");
        }

        // Check if layer is topmost, run "beforeClose"
        // Note: Layer.reasonForClose will contain 'escape' or 'glassClick' when those actions are responsible for closing the window.
        // You may also provide a custom reason, code, or other data to be attached to Layer.reasonForClose by passing it into close.
        layer.close = function (reason)
        {
            if (!layer || layer.closing)
            {
                return false;
            }

            layer.reasonForClose = reason;
            layer.closing = true;

            // Run beforeClose() if defined
            if (typeof layer.beforeClose === "function")
            {
                if (layer.beforeClose(layer, reason) === false)
                {
                    layer.reasonForClose = undefined;
                    layer.closing = false;
                    return false;
                }
            }
            var i;

            //Remove bindings
            for (i = 0; i < layer.options.bindings.length; i++)
            {
                var binding = layer.options.bindings[i];

                var bindingPoints = [];
                if (binding.bindTo !== undefined)
                {
                    bindingPoints = $(binding.bindTo);
                }
                if (bindingPoints.length === 0)
                {
                    bindingPoints = layer._bindingPoints;
                }

                if (binding.inSelectors)
                {
                    bindingPoints.off(binding.onEvents, binding.inSelectors, null, binding.runFunction);
                }
                else
                {
                    bindingPoints.off(binding.onEvents, binding.runFunction);
                }
            }


            var elementData;
            //re-expose elements from original DOM
            for (i = 0; i < layer._exposedElements.length; i++)
            {
                elementData = layer._exposedElements[i];
                elementData.element.css(
                    {
                        zIndex: elementData.previousZIndex,
                        position: elementData.previousPosition,
                        opacity: elementData.previousOpacity
                    })
                    .removeClass("exposedToModalLayer_" + layer.uniqueId)
                    .removeClass("currentlyExposed");

                elementData.element.off("wheel", preventScrollingOnExposedElements);
            }

            for (i = 0; i < layer._exposedByUnderlyingLayer.length; i++)
            {
                elementData = layer._exposedByUnderlyingLayer[i];
                elementData.element.css(
                    {
                        zIndex: elementData.previousZIndex
                    })
                    .addClass("currentlyExposed");
            }

            if (!skyward.global.mobileAccess)
            {
                skyward.dialogFocusManager.unregisterLayer(layer, true);
            }

            layer.container.removeData("layer");
            layer.container.off();
            layer.container.remove();
            layer.onscreen = false;

            if (typeof layer.options.afterClose === 'function')
            {
                layer.options.afterClose(layer, reason);
            }

            if (layer.options.resizeCallback && typeof layer.options.resizeCallback === 'function')
            {
                skyward.window.removeLayoutResizeCallback(layer.options.resizeCallback);
            }

            if (layer.options.loadDOMCallback && typeof layer.options.resizeCallback === 'function')
            {
                skyward.window.removeLoadDOMCallback(layer.options.loadDOMCallback);
            }

            layer = null;

            return true;
        };

        layer.container.appendTo(document.body);

        layer.container.data("_modalLayerDebug",
            {
                name: layer.options.debugName,
                creator: skyward.error.getCallerLocation()
            });

        // To define a custom element for use as this modal layer's glass (the barrier separating this layer from it's older siblings),
        //      provide the html to render the glass in the options parameter as options.glass
        //          (i.e. {glass:"<div class='notNormalGlassClass'/>"})
        // Note that the id of a layer's glass will ALWAYS be 'modalLayerGlass##' (where ## is the layer id#)
        layer.addContent($(layer.glass));

        // In some situations, you may need to "expose" elements from the original view, making them clickable on the new modal layer.
        // To do this, create an array of the elements to expose (either as jQuery objects, DOM elements, or jQuery selector strings) 
        // and pass this array to modalLayer.newModalLayer as options.expose[].
        // (More info inside closure)
        //Expose DOM elements

        // Due to z-indexing recurring nightmares, all *currently* non-exposed elements must have no z-index
        $(".currentlyExposed").each(function ()
        {
            var jqThis = $(this);
            layer._exposedByUnderlyingLayer.push(
                {
                    "element": jqThis,
                    "previousZIndex": jqThis.css("z-index")
                });

            jqThis.css("z-index", "auto").removeClass("currentlyExposed");
        });

        if (layer.options.expose)
        {
            for (const x of layer.options.expose)
            {
                var exposedClassName = "exposedToModalLayer_" + layer.uniqueId;
                $(x)
                    .addClass(exposedClassName)
                    .addClass("currentlyExposed")
                    .each(function ()
                    {
                        var jqThis = $(this);
                        layer._exposedElements.push(
                            {
                                "element": jqThis,
                                "previousOpacity": jqThis.css("opacity"),
                                "previousPosition": jqThis.css("position"),
                                "previousZIndex": jqThis.css("z-index")
                            });

                        // (MattE) Opacity explicitly set to create new "stacking context". For more info, see w3c spec on opacity
                        // or refer to http://philipwalton.com/articles/what-no-one-told-you-about-z-index/ 
                        jqThis.css({ "z-index": 1, "position": "relative", "opacity": 0.999999 });

                        jqThis.find(":focusable").addClass(exposedClassName);

                        layer._bindingPoints.push(jqThis.parent());

                        jqThis.on("wheel", preventScrollingOnExposedElements);
                    });
            }
        }

        layer.contents = $(contents);
        layer.addContent(layer.contents);

        // Establish bindings
        // To bind custom events to the layer's contents, define each binding inside options.bindings[] 
        //  Example:
        //          myLayerOptions = {
        //              bindings: [
        //                  {onEvents:"click", inSelectors:".button", runFunction: myCustomButtonClickHandler},
        //                  {onEvents:"keypress", inSelectors:"input", runFunction: myCustomFilterFunction}
        //              ]};
        //          skyward.modalLayer.newModalLayer(myContent, null, myLayerOptions)
        //
        // Note: Because bindings are attached to the *container* through jQuery.on(), your elements do NOT need to be attached to the DOM at bind-time :)

        // A binding is defined as {onEvents, inSelectors, runFunction [, directBind]}
        // onEvents:
        //      Space-delimited list of events which will trigger this binding. i.e. "click keydown keypress"
        // inSelectors:
        //      A jQuery-style selector to filter the descendants of the layer container. i.e. "span.clickable", "a, input.clickable", etc
        //      The descendants in question do NOT have to exist in the DOM at bind-time for these bindings to work.
        //      (This is a feature built into jQuery.on(), read about it at http://api.jquery.com/on/ )
        // runFunction:
        //      The function to call when one of these events bubbles to the container from an element matching the selector.             
        // bindTo:
        //      To specify an element other than the layer container as the binding-element for an event-handler, set this value to 
        //      either a jQuery object containing the reference(s) to the element, or a selector string.
        if (layer.options.closeOnClick === true || !skyward.global.mobileAccess)
        {
            layer.glass.on("click", function ()
            {
                if (!layer)
                {
                    return;
                }

                if (layer.options.closeOnClick === true)
                {
                    layer.close('glassClick');
                }
                else if (!skyward.global.mobileAccess)
                {
                    skyward.dialogFocusManager.setFocus(layer);
                }
            });
        }

        if (layer.options.preventKeyBubbling)
        {
            layer.container.on("keydown keyup", function (event)
            {
                var eventKeys = skyward.keys.getKeyNamesFromEvent(event);
                return eventKeys.TAB || eventKeys.ENTER;
            });
        }

        if (layer.options.closeOnEscape === true)
        {
            layer.options.bindings.push(
                {
                    onEvents: 'keydown',
                    bindTo: document.body,
                    runFunction: function (event)
                    {
                        var eventKeys = skyward.keys.getKeyNamesFromEvent(event);
                        if (eventKeys.ESC)
                        {
                            if (event.currentPopupId === undefined || event.currentPopupId === null)
                            {
                                event.currentPopupId = skyward.window.getCurrentPopupId();
                            }

                            if (layer.uniqueId === event.currentPopupId)
                            {
                                layer.close('escape');
                            }
                            return false;
                        }
                    }
                });
        }

        if (layer.options.bindings.length)
        {
            layer._bindingPoints.push(layer.container);
            layer._bindingPoints = $($.uniqueSort(layer._bindingPoints));
            for (var i = 0; i < layer.options.bindings.length; i++)
            {
                var binding = layer.options.bindings[i];

                var bindingPoints = [];
                if (binding.bindTo !== undefined)
                {
                    bindingPoints = $(binding.bindTo);
                }
                if (bindingPoints.length === 0)
                {
                    bindingPoints = layer._bindingPoints;
                }

                if (binding.inSelectors)
                {
                    bindingPoints.on(binding.onEvents, binding.inSelectors, null, binding.runFunction);
                }
                else
                {
                    bindingPoints.on(binding.onEvents, binding.runFunction);
                }
            }
        }

        layer.container.data("layer", layer);
        layer.onscreen = true;

        if (!skyward.global.mobileAccess)
        {
            skyward.dialogFocusManager.registerLayer(layer);
        }

        return layer;
    };

    /**
     * Retrieve the ModalLayer object by passing in an element within the modal.
     *
     * @param {(HTMLElement | JQuery | string)} sourceElement - An element/selector within a modal
     * @return {ModalLayer} The ModalLayer for the provided element
     */
    skyward.modalLayer.getLayerFromElement = function (sourceElement)
    {
        var element = $(sourceElement);
        var container = element.hasClass("modalLayerContainer") ? element : element.closest(".modalLayerContainer");
        return container.data("layer");
    };

    /**
     * Create a PositionManager object to control where a ModalDialog is positioned
     *
     * @param {ModalDialog} dialog - A ModalDialog that will have its contents positioned
     * @param {(HTMLElement | JQuery | string)} anchorTo - The element to anchor the ModalDialog.contents to
     * @param {JQuery} content - A reference to the ModalDialog.contents
     * @param {object} positioningOptions - An object with configuration options for the PositioningManager
     *                                      TODO: PR 4158200 - Document the positioningOptions
     * @return {PositionManager} A PositioningManager object
     */
    skyward.modalLayer.createModalPositioningManager = function (dialog, anchorTo, content, positioningOptions)
    {
        var ANCHORED_TAIL_HEIGHT = 13;
        var MINIMUM_CONTENT_OUTER_HEIGHT = 60;
        var WINDOW_PADDING = 20;

        var positioningSettings = $.extend(
            {
                maintainRelativePosition: false
            }, positioningOptions);

        var cache = {};
        var container = dialog.container;
        var minimumContentCssHeight = parseInt(content.css('min-height'));

        var canSetHeight = false;
        var positionBelowAnchorTo = null;
        if (!(anchorTo instanceof $))
        {
            anchorTo = $(anchorTo);
        }

        var positioningManager =
        {
            measurements:
            {
                allowedContentHeight: 0,
                contentOnly: 0,
                containerWithoutContent: 0,
                entireContainer: 0,
                top: 0
            },
            positionModalContents: function ()
            {
                positioningManager.isAnchored() ?
                    positionAnchoredLayer() :
                    positionUnanchoredLayer();
            },
            positionEELContents: function ()
            {
                positioningManager.isAnchored() ? setAnchorToOffset() : dialog.close();

                var eelPositioningSettings = $.extend(
                    {
                        getHeaderRow: function ()
                        {
                            if (browse.header === undefined || browse.header === null)
                            {
                                return $();
                            }
                            return browse.header.find(".unlockedHeaderTableRow");
                        },
                        getRows: function ()
                        {
                            if (browse.browse === undefined || browse.browse === null )
                            {
                                return $();
                            }
                            return browse.browse.find(".js-eelPositionManagerGetRowsForHeight");
                        },
                        minimumBrowseWidth: 0,
                        minContentHeight: 50
                    },
                    positioningSettings);

                content.width("");
                container.css("display", "inherit");
                container.addClass("anchored");

                var anchoredLayerCssPosition = {};
                var availableSpace;
                var heightOfAllRows = 0;
                var heightOfContentThatCantFit;

                var browse = getBrowseFromCache(content);
                var heightOfRequiredParts = getHeightOfRequiredParts(browse);

                var headerRow = eelPositioningSettings.getHeaderRow();
                var rows = eelPositioningSettings.getRows();

                if (rows.length > 0)
                {
                    var minimumHeightInfo =
                    {
                        rowsFoundForMinHeight: 0,
                        heightOfOneRow: 0,
                        heightOfFoundMinRows: 0
                    };

                    rows.each(function ()
                    {
                        var row = $(this);
                        var rowHeight = row.outerHeight(true);
                        if (positioningOptions.minRowsToShow && minimumHeightInfo.rowsFoundForMinHeight < positioningOptions.minRowsToShow)
                        {
                            minimumHeightInfo = getMinimumHeightInfo(rowHeight, minimumHeightInfo);
                        }
                        heightOfAllRows += rowHeight;
                    });

                    // The minimum body height should be at least 3 rows tall.
                    if (positioningOptions.minRowsToShow)
                    {
                        if (minimumHeightInfo.rowsFoundForMinHeight > 0 && minimumHeightInfo.rowsFoundForMinHeight < positioningOptions.minRowsToShow)
                        {
                            var rowsNeedingHeight = positioningOptions.minRowsToShow - minimumHeightInfo.rowsFoundForMinHeight;
                            eelPositioningSettings.minContentHeight = minimumHeightInfo.heightOfFoundMinRows;
                            eelPositioningSettings.minContentHeight += minimumHeightInfo.heightOfOneRow * rowsNeedingHeight;
                        }
                        else if (minimumHeightInfo.rowsFoundForMinHeight !== positioningOptions.minRowsToShow)
                        {
                            var standardRowHeight = 33;
                            eelPositioningSettings.minContentHeight = standardRowHeight * positioningOptions.minRowsToShow;
                        }
                    }
                }

                function getBrowseFromCache(content)
                {
                    if (cache.browseID === undefined)
                    {
                        const browse = skyward.browse.getInnerBrowse(content);
                        if (browse !== undefined)
                        {
                            cache.browseID = browse.id;
                        }
                    }
                    return skyward.browse.browseList[cache.browseID];
                }

                function getHeightOfRequiredParts(browse)
                {
                    var footerHeight = 0;

                    if (cache.browseControlsHeight === undefined || cache.browseControlsHeight === null)
                    {
                        if (browse !== undefined && browse !== null)
                        {
                            // if browse.controls isn't available, don't try and cache the height
                            if (browse.controls !== undefined && browse.controls !== null)
                            {
                                cache.browseControlsHeight = parseInt(browse.controls.outerHeight(true) || 0);
                            }
                        }
                        else
                        {
                            cache.browseControlsHeight = 0;
                        }
                    }
                    if (cache.browseHeaderHeight === undefined || cache.browseHeaderHeight === null)
                    {
                        if (browse !== undefined && browse !== null)
                        {
                            // if browse.controls isn't available, don't try and cache the height
                            if (browse.header !== undefined && browse.header !== null)
                            {
                                cache.browseHeaderHeight = parseInt(browse.header.outerHeight(true) || 0);
                            }
                        }
                        else
                        {
                            cache.browseHeaderHeight = 0;
                        }
                    }

                    if (browse !== undefined && browse !== null && browse.footer !== undefined && browse.footer !== null)
                    {
                        // footer will not appear for 0 results, due to variability and the fact that there can be rows that do not appear to the user, like CMA message rows
                        // it is best not to cache this right now without further work to detect both scenarios.
                        footerHeight = parseInt(browse.footer.outerHeight(true) || 0);
                    }

                    var heightOfRequiredParts = (cache.browseControlsHeight || 0) + (cache.browseHeaderHeight || 0) + footerHeight;
                    return heightOfRequiredParts;
                }

                function getMinimumHeightInfo(rowHeight, minimumHeightInfo)
                {
                    var newMinimumHeightInfo = {};
                    if (rowHeight > 0)
                    {
                        newMinimumHeightInfo.heightOfFoundMinRows = minimumHeightInfo.heightOfFoundMinRows += rowHeight;
                        newMinimumHeightInfo.rowsFoundForMinHeight = minimumHeightInfo.rowsFoundForMinHeight += 1;
                        newMinimumHeightInfo.heightOfOneRow = rowHeight;
                    }
                    return newMinimumHeightInfo;
                }

                if (heightOfAllRows < eelPositioningSettings.minContentHeight)
                {
                    heightOfAllRows = eelPositioningSettings.minContentHeight;
                }
                positionBelowAnchorTo = getPositionBelowAnchorTo();
                if (positionBelowAnchorTo)
                {
                    availableSpace = skyward.window.clientHeight - (positioningManager.anchorToOffset?.bottom ?? 0) - WINDOW_PADDING;

                    var scrollbarPadding = 0;
                    if (browse !== undefined && browse !== null && skyward.window.hasHorizontalScrollBar(browse.unlockedBody))
                    {
                        scrollbarPadding = skyward.window.scrollbarHeight;
                    }

                    var desiredHeightOfCodeEELBrowse = heightOfRequiredParts + heightOfAllRows + scrollbarPadding;
                    if (availableSpace > desiredHeightOfCodeEELBrowse)
                    {
                        content.height(desiredHeightOfCodeEELBrowse);
                    }
                    else
                    {
                        var widthBeforeResize = content.width();
                        content.height(availableSpace);
                        if (content.width() == widthBeforeResize)
                        {
                            // Some browsers like FireFox add the scrollbar inside the width of content.
                            // The preferred way is Chrome which adds the scrollbar to the content so that the width of content remains the same.
                            content.width(content.width() + skyward.window.scrollbarHeight);
                        }
                    }
                    anchoredLayerCssPosition.top = positioningManager.anchorToOffset?.bottom ?? 0;
                }
                else
                {
                    availableSpace = positioningManager.anchorToOffset.top - WINDOW_PADDING;
                    var contentHeight = heightOfRequiredParts + heightOfAllRows;

                    if (availableSpace >= heightOfRequiredParts + heightOfAllRows)
                    {
                        content.height(contentHeight);
                        anchoredLayerCssPosition.top = positioningManager.anchorToOffset.top - contentHeight;
                    }
                    else
                    {
                        heightOfContentThatCantFit = contentHeight - availableSpace;
                        var adjustedContentHeight = contentHeight - heightOfContentThatCantFit;
                        content.height(adjustedContentHeight);
                        anchoredLayerCssPosition.top = positioningManager.anchorToOffset.top - adjustedContentHeight;
                    }
                }

                var displayLeftOfElement = (skyward.window.clientWidth - positioningManager.anchorToOffset.left) < skyward.window.clientWidth / 2;

                var containerBorder = 2;
                var rowWidth = headerRow.outerWidth(true) + containerBorder;
                var browseWidth;

                if (!rowWidth || rowWidth < eelPositioningSettings.minimumBrowseWidth)
                {
                    browseWidth = eelPositioningSettings.minimumBrowseWidth;
                }
                else
                {
                    browseWidth = rowWidth;
                }

                if (displayLeftOfElement)
                {
                    var availableSpaceLeft = positioningManager.anchorToOffset.right - WINDOW_PADDING;
                    if (availableSpaceLeft < eelPositioningSettings.minimumBrowseWidth || availableSpaceLeft < rowWidth)
                    {
                        content.outerWidth(availableSpaceLeft);
                        anchoredLayerCssPosition.left = WINDOW_PADDING;
                    }
                    else
                    {
                        content.outerWidth(browseWidth);
                        anchoredLayerCssPosition.left = positioningManager.anchorToOffset.right - browseWidth;
                    }
                }
                else
                {
                    var availableSpaceRight = skyward.window.clientWidth - positioningManager.anchorToOffset.left - WINDOW_PADDING;
                    if (availableSpaceRight < eelPositioningSettings.minimumBrowseWidth || availableSpaceRight < rowWidth)
                    {
                        content.outerWidth(availableSpaceRight);
                    }
                    else
                    {
                        content.outerWidth(Math.ceil(skyward.window.hasVerticalScrollBar(content) ? browseWidth + skyward.window.scrollbarHeight : browseWidth));
                    }
                    anchoredLayerCssPosition.left = positioningManager.anchorToOffset.left;
                }
                content.css(anchoredLayerCssPosition);
            }
        };

        if (dialog.animationPromise)
        {
            dialog.animationPromise.done(function ()
            {
                canSetHeight = true;
            });
        }
        else
        {
            canSetHeight = true;
        }

        var getMinimumModalHeight = function ()
        {
            var minimumModalHeight = minimumContentCssHeight ? minimumContentCssHeight : MINIMUM_CONTENT_OUTER_HEIGHT;
            minimumModalHeight += positioningManager.measurements.containerWithoutContent;
            return minimumModalHeight;
        };

        var setAllowedAnchoredContentHeight = function (positionBelowAnchorTo)
        {
            var differenceInHeights;
            var originalModalHeightCanFit;
            var availableBottomContentSpace;
            var availableBottomSpace;
            var adjustedContentHeight;
            var totalModalHeight = positioningManager.measurements.entireContainer;
            var minimumModalHeight = getMinimumModalHeight();

            if (positionBelowAnchorTo)
            {
                availableBottomSpace = skyward.window.clientHeight - (positioningManager.anchorToOffset?.bottom ?? 0) - WINDOW_PADDING;
                originalModalHeightCanFit = totalModalHeight < availableBottomSpace;
                availableBottomContentSpace = availableBottomSpace - positioningManager.measurements.containerWithoutContent;
            }
            else
            {
                originalModalHeightCanFit = totalModalHeight < positioningManager.anchorToOffset.top;
                differenceInHeights = totalModalHeight - positioningManager.anchorToOffset.top;
            }

            if (originalModalHeightCanFit)
            {
                positioningManager.measurements.allowedContentHeight = positioningManager.measurements.contentOnly;
            }
            else
            {
                adjustedContentHeight = positionBelowAnchorTo ? availableBottomContentSpace : positioningManager.measurements.contentOnly - differenceInHeights;
                adjustedContentHeight -= WINDOW_PADDING;
                positioningManager.measurements.allowedContentHeight = (minimumModalHeight > adjustedContentHeight) ? minimumModalHeight : adjustedContentHeight;
            }
        };

        positioningManager.isAnchored = function ()
        {
            return anchorTo.length && anchorTo.is(":visible");
        };

        var setAnchorToOffset = function ()
        {
            positioningManager.anchorToOffset = anchorTo.offset();
            positioningManager.anchorToOffset.right = positioningManager.anchorToOffset.left + anchorTo.outerWidth();
            positioningManager.anchorToOffset.bottom = positioningManager.anchorToOffset.top + anchorTo.outerHeight();
        };

        var getPositionBelowAnchorTo = function ()
        {
            if (positioningSettings.maintainRelativePosition !== true)
            {
                return calculatePositionBelowAnchorTo();
            }

            if (cache.positionBelowAnchorTo === undefined)
            {
                cache.positionBelowAnchorTo = calculatePositionBelowAnchorTo();
            }

            return cache.positionBelowAnchorTo;

            function calculatePositionBelowAnchorTo()
            {
                var spaceBelowAnchorTo = skyward.window.clientHeight - (positioningManager.anchorToOffset?.bottom ?? 0);
                var spaceAboveAnchorTo = positioningManager.anchorToOffset.top;

                return spaceBelowAnchorTo >= spaceAboveAnchorTo;
            }
        };

        var setMeasurements = function ()
        {
            var originalHeight = content.height();
            var originalScrollTop = content.scrollTop();

            content.height("");

            positioningManager.measurements.contentOnly = content.outerHeight(true);

            // sum up the height of all non content parts since the modal container is positioned absolutely
            positioningManager.measurements.containerWithoutContent = 0;
            container.contents().not(".modal.content").each(function ()
            {
                positioningManager.measurements.containerWithoutContent += $(this).outerHeight(true);
            });

            var containerWidth = container.outerWidth(true);
            var contentWidth = content.outerWidth(true);

            positioningManager.measurements.containerOuterWidth = containerWidth > contentWidth ? containerWidth : contentWidth;
            positioningManager.measurements.entireContainer = positioningManager.measurements.contentOnly + positioningManager.measurements.containerWithoutContent;

            content.height(originalHeight);
            content.scrollTop(originalScrollTop);
        };

        var positionUnanchoredLayer = function ()
        {
            dialog.container.css('top', '');
            dialog.container.css('left', '');
            dialog.container.css('position', 'relative');
            dialog.content.css('height', '');
        };

        var positionAnchoredLayer = function ()
        {
            setAnchorToOffset();

            container.css("display", "inherit");
            container.addClass("anchored");
            dialog.container.css('position', '');

            var displayAboveElement = false;
            var displayLeftOfElement = false;
            var arrowMiddleOffset = 14; // 14 = half of arrow width (28)
            var arrowIndentPercent = 5; // % in from the left or right where we start the arrow
            var anchorToWidth = anchorTo.outerWidth();
            var containerOuterWidth = container.outerWidth(true);
            var arrowPosition = (containerOuterWidth * (arrowIndentPercent / 100)) + arrowMiddleOffset;
            var minimumSpacingFromEdgeOfScreen = 10; // we don't want an anchored dialog touching the edge of the screen

            var removeOldArrowPositionClasses = function ()
            {
                container.removeClass("topLeft topRight bottomLeft bottomRight");
            };

            var getArrowPositionClass = function ()
            {
                var arrowCSSPositionClasses = ["topLeft", "topRight", "bottomLeft", "bottomRight"];
                if (displayAboveElement && displayLeftOfElement)
                {
                    return arrowCSSPositionClasses[3];
                }
                else if (displayAboveElement && !displayLeftOfElement)
                {
                    return arrowCSSPositionClasses[2];
                }
                else if (!displayAboveElement && displayLeftOfElement)
                {
                    return arrowCSSPositionClasses[1];
                }
                return arrowCSSPositionClasses[0];
            };

            var position = function ()
            {
                var anchoredLayerCssPosition = {};

                const scrollTop = document.documentElement.scrollTop;
                const scrollLeft = document.documentElement.scrollLeft;

                // because the anchorTo is not visible to the user, we run through this unanchored position function and return
                if (positioningManager.anchorToOffset.left - scrollLeft >= skyward.window.clientWidth || positioningManager.anchorToOffset.left < 0 ||
                    positioningManager.anchorToOffset.top - scrollTop >= skyward.window.clientHeight || positioningManager.anchorToOffset.top < 0 ||
                    positioningManager.anchorToOffset.left - scrollLeft + arrowPosition + minimumSpacingFromEdgeOfScreen > skyward.window.clientWidth
                    // We don't check against the left side of the screen because row-level browse buttons can get very close to the edge.
                    // We'd rather the arrow be slightly off center, rather than in the middle of the screen.
                    /* || positioningManager.anchorToOffset.left - arrowPosition - minimumSpacingFromEdgeOfScreen < 0 */) {
                    renderAnchoredAsUnanchored();
                    return;
                }

                setMeasurements();
                positionBelowAnchorTo = getPositionBelowAnchorTo();
                setAllowedAnchoredContentHeight(positionBelowAnchorTo);

                if (positionBelowAnchorTo)
                {
                    anchoredLayerCssPosition.top = (positioningManager.anchorToOffset?.bottom ?? 0) + ANCHORED_TAIL_HEIGHT;
                }
                else
                {
                    displayAboveElement = true;
                    anchoredLayerCssPosition.top = positioningManager.anchorToOffset.top - (positioningManager.measurements.containerWithoutContent + positioningManager.measurements.allowedContentHeight + ANCHORED_TAIL_HEIGHT);
                }
                positioningManager.measurements.top = anchoredLayerCssPosition.top;

                if (canSetHeight)
                {
                    content.outerHeight(positioningManager.measurements.allowedContentHeight || 'auto');
                }

                if (skyward.window.clientWidth < positioningManager.anchorToOffset.left + containerOuterWidth)
                {
                    if (positioningManager.anchorToOffset.left < containerOuterWidth)
                    {
                        renderAnchoredAsUnanchored();
                        return;
                    }
                    displayLeftOfElement = true;
                    var clientWidthPadding = 2;
                    var distanceFromRight = (skyward.window.clientWidth - clientWidthPadding) - (positioningManager.anchorToOffset.left + (anchorToWidth / 2)) - arrowPosition;
                    anchoredLayerCssPosition.left = skyward.window.clientWidth - distanceFromRight - containerOuterWidth;
                }
                else
                {
                    var moduleContentPadding = 10;
                    displayLeftOfElement = false;
                    var anchoredLayerPositionLeft = positioningManager.anchorToOffset.left + (anchorToWidth / 2) - arrowPosition;

                    if (anchoredLayerPositionLeft < moduleContentPadding)
                    {
                        anchoredLayerPositionLeft = moduleContentPadding;

                        var arrowOffset = positioningManager.anchorToOffset.left + (anchorToWidth / 2) - anchoredLayerPositionLeft - arrowMiddleOffset;

                        if (!container.hasClass('customArrowPosition'))
                        {
                            $("<style type='text/css'> #" + container[0].id + ":before, #" + container[0].id + ":after { left: " + arrowOffset + "px; } </style>").appendTo(".modalLayerContainer");
                            container.addClass('customArrowPosition');
                        }
                    }

                    var positionLeftAndContainerWidth = anchoredLayerPositionLeft + containerOuterWidth;
                    if (positionLeftAndContainerWidth < skyward.window.clientWidth)
                    {
                        anchoredLayerCssPosition.left = anchoredLayerPositionLeft;
                    }
                    else
                    {
                        var overlapValue = positionLeftAndContainerWidth - skyward.window.clientWidth;
                        anchoredLayerCssPosition.left = anchoredLayerPositionLeft - overlapValue - WINDOW_PADDING;
                    }
                }
                
                container.css(anchoredLayerCssPosition);

                var dialogArrowClass = getArrowPositionClass();
                removeOldArrowPositionClasses(dialogArrowClass);
                container.addClass(dialogArrowClass);
            };

            position();

            function renderAnchoredAsUnanchored()
            {
                removeOldArrowPositionClasses();
                positionUnanchoredLayer();
            }
        };

        return positioningManager;
    };

    /**
     * Return true of the passed in element/selector is found within the ModalLayer
     *
     * @param {(HTMLElement | JQuery | string)} selector - An element/selector to try to find within the current ModalLayer
     * @return {boolean} Was the element found within the ModalLayer
     */
    skyward.modalLayer.elementIsInModalLayer = function (selector)
    {
        selector = selector instanceof $ ? selector : $(selector);
        return selector.closest(".modalLayerContainer").length > 0;
    };

    function concatAdditionalClasses(object, value)
    {
        if (object === null || typeof object === 'undefined')
        {
            object = {};
        }

        if (typeof object.additionalClasses === 'undefined')
        {
            object.additionalClasses = value;
        }
        else
        {
            object.additionalClasses = (object.additionalClasses + " " + value).trim();
        }

        return object;
    }
    function preventScrollingOnExposedElements(e)
    {
        e.preventDefault();
        return false;
    }

    skyward.error.registerErrorDataGatherer("modallayer", function ()
    {
        var layers = [];
        $(".modalLayerContainer").toArray().forEach(function (containerElem)
        {
            var data = $(containerElem).data() || {};
            layers[data.layerid] = data._modalLayerDebug || null;
        });
        return layers;
    });

}(window.skyward = window.skyward || {}, window.jQuery));
